import { Props as ButtonWebProps } from '@rover/kibble/core/Button/Button';
import { ButtonSizes } from '@rover/kibble/core/Button/Button.common';
import { Props as ButtonNativeProps } from '@rover/kibble/core/Button/Button.native';

export type ButtonWithCountBadgeCommonProps = {
  /**
   * The number in the badge. `0` or no value hides the badge. A count over 99 shows as "99+", so
   * put the exact count in `ariaLabel`.
   */
  badgeCount?: number;
  /**
   * The button's accessible name. Button throws during development without it. The count isn't
   * part of the name unless you include it.
   */
  ariaLabel?: string;
  /** The badge's test id: `data-testid` on web, `testID` on React Native. */
  badgeTestID?: string;
  /**
   * Hides the badge from screen readers on web and React Native. Set it whenever `ariaLabel`
   * includes the count. Defaults to `false`.
   */
  hideBadgeFromScreenReader?: boolean;
};

export const BUTTON_SIZE_TO_BADGE_SIZE_MAP: Record<ButtonSizes, number> = {
  small: 16,
  default: 24,
  large: 24,
};

// Offset of a circle badge from the button's top-right corner, per Figma. Pills grow rightward from the same left edge.
export const BUTTON_SIZE_TO_BADGE_OFFSET_MAP: Record<ButtonSizes, { top: number; right: number }> =
  {
    small: { top: 0, right: -3 },
    default: { top: -4, right: -2 },
    large: { top: -4, right: -2 },
  };

// Width of the borderWidth.primary ring, for positioning math. Figma draws the ring outside the fill.
export const BADGE_RING_PX = 2;

// Averta sits digits ~0.0388em low when centered; this lifts them to optical center.
export const BADGE_DIGIT_OPTICAL_OFFSET = -0.5;

export const MAX_BADGE_COUNT = 99;

/** The badge's text: the count, or "99+" above `MAX_BADGE_COUNT`. */
export const getBadgeLabel = (badgeCount: number | undefined): string => {
  if (badgeCount && badgeCount > MAX_BADGE_COUNT) return `${MAX_BADGE_COUNT}+`;
  return String(badgeCount ?? '');
};

// Figma grows the badge into a pill at two characters on small and at three on the other sizes.
export const isPillBadge = (badgeLabel: string, size: ButtonSizes): boolean =>
  badgeLabel.length > (size === 'small' ? 1 : 2);

export type ButtonWithCountBadgeNativeProps = ButtonWithCountBadgeCommonProps & ButtonNativeProps;

export type ButtonWithCountBadgeWebProps = ButtonWithCountBadgeCommonProps & ButtonWebProps;
